import { Choice, Customization } from "./api-models/order-pd-response.model";
import { AdvanceBillPaymentData } from "./workflow/advance-bill-payment.model";
import { AlternateShippingAddressData } from "./workflow/alternate-shipping-address.model";
import { CreditCheckData } from "./workflow/credit-check.model";
import { CustomerAccountSetupData } from "./workflow/cutomer-account-setup.model";
import { InstallationChecklistData } from "./workflow/installation-checklist.model";
import { InstallationSchedulingData } from "./workflow/installation-scheduling.model";
import { InternetFlow } from "./workflow/internet/internet-flow.model";
import { OrderChecklistData } from "./workflow/order-checklist.model";
import { OrderSetupData } from "./workflow/order-setup.model";
import { OrderSummaryData } from "./workflow/order-summary.model";
import { PaymentInformationEntryData } from "./workflow/payment-information-entry.model";
import { PreviousAddressData } from "./workflow/previous-address.model";
import { ProductSummaryData } from "./workflow/product-summary.model";
import { ServiceInfoCollectionData } from "./workflow/service-info-collection.model";
import { TermsAndConditionData } from "./workflow/terms-conditions.model";
import { VideoFlow } from "./workflow/video/video-flow.model";
import { VoiceFlow } from "./workflow/voice/voice-flow.model";
import { WirelessCartCustomizationType, LineConfigType } from "./workflow/wireless/wireless-cart.model";
import { WirelessFlow } from "./workflow/wireless/wireless-flow.model";

export interface ProductTracker {
    key: ServiceType;
    status: ProductStatusType;
    available: boolean;
    priority?: number;
}

export const PRODUCT_TYPES = {
    INTERNET: 'INTERNET',
    VIDEO: 'VIDEO',
    WIRELESS: 'WIRELESS',
    PROTECTION: 'PROTECTION',
    VOICE: 'VOICE',
    VOIP: 'VOIP' // VOIP is used in API responses for voice products, but the app uses VOICE internally
} as const;

export const FlowTypes = {
    INTERNET: 'Internet',
    VIDEO: 'Video',
    WIRELESS: 'Wireless',
    VOICE: 'Voice'
} as const;

export type ServiceType = typeof PRODUCT_TYPES[keyof typeof PRODUCT_TYPES];

export type Flow = typeof FlowTypes[keyof typeof FlowTypes];

export const PRODUCT_STATUS = {
    AVAILABLE_NOT_STARTED: 'AVAILABLE_NOT_STARTED',
    COMPLETED: 'COMPLETED',
    STARTED_BUT_NOT_FINISHED: 'STARTED_BUT_NOT_FINISHED',
    REQUIRES_ATTENTION: 'REQUIRES_ATTENTION',
    NOT_AVAILABLE: 'NOT_AVAILABLE'
} as const;

export enum ProductStatusType {
    AVAILABLE_NOT_STARTED = 'AVAILABLE_NOT_STARTED',
    COMPLETED = 'COMPLETED',
    STARTED_BUT_NOT_FINISHED = 'STARTED_BUT_NOT_FINISHED',
    REQUIRES_ATTENTION = 'REQUIRES_ATTENTION',
    NOT_AVAILABLE = 'NOT_AVAILABLE'
}

export enum StepStatusType {
    Completed = 'completed',
    InProgress = 'inProgress',
    NotStarted = 'notStarted',
    RequiresAttention = 'requiresAttention',
    NotAvailable = 'notAvailable',
    AvailableNotStarted = 'availableNotStarted',
    StartedButNotFinished = 'startedButNotFinished'
}

export enum FlowStatusType {
    AVAILABLE_NOT_STARTED = 'AVAILABLE_NOT_STARTED',
    COMPLETED = 'COMPLETED',
    STARTED_BUT_NOT_FINISHED = 'STARTED_BUT_NOT_FINISHED',
    REQUIRES_ATTENTION = 'REQUIRES_ATTENTION',
    NOT_AVAILABLE = 'NOT_AVAILABLE'
}


export enum OrderStatusType {
    Draft = 'draft',
    Submitted = 'submitted'
}

export enum OrderFlowType {
    Internet = 'Internet',
    Wireless = 'Wireless',
    Video = 'Video',
    Voice = 'Voice'
    // Add other flow types as needed
}

export enum ProductType {
    Internet = 'Internet',
    Wireless = 'Wireless',
    Video = 'Video',
    Voice = 'Voice',
    OOF = 'OOF' // Out of Foot Prints
}

/** API returns 'VOIP' for voice products (SQ Type, PD ProductType); the app uses VOICE internally. */
export const API_VOICE_PRODUCT_TYPE = 'VOIP';

export function isVoiceProductType(apiType: string | null | undefined): boolean {
    const type = apiType?.toUpperCase();
    return type === 'VOICE' || type === API_VOICE_PRODUCT_TYPE;
}

export interface OrderV2 {
    orderNumber: string;
    agentId?: string;
    status?: OrderStatusType;
    flowType?: OrderFlowType;
    lastSavedAt?: string;
    orderSetup?: OrderSetupData;
    serviceInfoCollection?: ServiceInfoCollectionData;
    internetFlow?: InternetFlow;
    wirelessFlow?: WirelessFlow; // Define wireless flow data structure later
    videoFlow?: VideoFlow; // Define video flow data structure later
    voiceFlow?: VoiceFlow;
    creditCheck?: CreditCheckData;
    previousAddress?: PreviousAddressData;
    advanceBillPayment?: AdvanceBillPaymentData; // Define advance bill payment data structure later
    customerAccountSetup?: CustomerAccountSetupData,
    alternateShippingAddress?: AlternateShippingAddressData;
    orderSummary?: OrderSummaryData;
    installationScheduling?: InstallationSchedulingData;
    installationChecklist?: InstallationChecklistData;
    currentRoute?: string;
    productSummary?: ProductSummaryData
    serviceStatuses?: ProductTracker[];
    termsAndConditions?: TermsAndConditionData; // Define terms and conditions data structure later
    orderChecklist?: OrderChecklistData; // Define order checklist data structure later
    paymentInformationEntry?: PaymentInformationEntryData; // Define payment information entry data structure later
    // Add internet, wireless, etc. later
}

export interface BaseFlowData {
    status: StepStatusType;
    flowPriority: number;
    lastSavedAt?: string;
}

export interface OrderSelectedCustomization {
    internet: {
        Customization: Customization;
        Choice: Choice;
    },
    wireless: {
        lineNumber?: number;
        type: WirelessCartCustomizationType;
        lineType?: LineConfigType;
    }
}

export interface SelectedCustomization {
    Customization: Customization;
    Choice: Choice;
}

export interface OrderInternetCustomization extends SelectedCustomization {

}

export interface OrderWirelessCustomization extends SelectedCustomization {
    lineNumber?: number;
    type: WirelessCartCustomizationType;
    lineType?: LineConfigType;
}


